/**
 * Pure graph-traversal utilities for the lineage canvas.
 *
 * No React / React Flow imports — this module is unit-tested in isolation and
 * consumed by the reducer/selector. See plan §B2.5 (group-boundary), §B2.6
 * (directional/bidirectional/cycle) and §B2.7 (element-level) for the rules.
 *
 * Direction semantics on a system edge:
 *   forward        : data flows source -> target
 *   backward       : data flows target -> source
 *   bidirectional  : both ways (edge is in BOTH the up- and downstream graphs)
 */

/** Composite key for a data element. tableId scopes it to a database node's table. */
export const elKey = (nodeId, elementId, tableId) => `${nodeId}::${tableId || ''}::${elementId}`;

/**
 * Map every group node to the Set of its transitive member node ids
 * (children, grandchildren, …). Used to expand a group into its members so an
 * edge touching a group is treated as touching all members (§B2.5).
 */
export const buildGroupMembers = (nodes) => {
  const childrenByParent = new Map();
  nodes.forEach((n) => {
    if (!n.parentId) return;
    if (!childrenByParent.has(n.parentId)) childrenByParent.set(n.parentId, []);
    childrenByParent.get(n.parentId).push(n.id);
  });
  const members = new Map();
  const collect = (id, acc) => {
    (childrenByParent.get(id) || []).forEach((childId) => {
      if (acc.has(childId)) return;
      acc.add(childId);
      collect(childId, acc);
    });
  };
  nodes.forEach((n) => {
    if (n.kind === 'group') {
      const acc = new Set();
      collect(n.id, acc);
      members.set(n.id, acc);
    }
  });
  return members;
};

/** {id} ∪ transitive members (if id is a group). */
const expand = (id, groupMembers) => {
  const set = new Set([id]);
  const members = groupMembers.get(id);
  if (members) members.forEach((m) => set.add(m));
  return set;
};

/**
 * Build the downstream adjacency (Map<nodeId, [{to, edgeId}]>). Pass mirror=true
 * for the upstream graph. Group endpoints are expanded on both sides so a
 * group↔node / group↔group edge connects every member (§B2.5).
 */
const buildAdjacency = (edges, groupMembers, mirror) => {
  const adj = new Map();
  const link = (from, to, edgeId) => {
    if (!adj.has(from)) adj.set(from, []);
    adj.get(from).push({to, edgeId});
  };
  edges.forEach((e) => {
    const pairs = [];
    // pair = [downstreamFrom, downstreamTo]
    if (e.direction === 'backward') pairs.push([e.target, e.source]);
    else if (e.direction === 'bidirectional') pairs.push([e.source, e.target], [e.target, e.source]);
    else pairs.push([e.source, e.target]); // forward / default
    pairs.forEach(([a, b]) => {
      const from = mirror ? b : a;
      const to = mirror ? a : b;
      const fromSet = expand(from, groupMembers);
      const toSet = expand(to, groupMembers);
      fromSet.forEach((fx) => toSet.forEach((tx) => link(fx, tx, e.id)));
    });
  });
  return adj;
};

/** Breadth-first walk. depth=null → unlimited; depth=1 → immediate neighbours only. */
const bfs = (adj, seed, depth) => {
  const nodes = new Set(seed);
  const edges = new Set();
  let frontier = [...seed];
  let level = 0;
  while (frontier.length && (depth == null || level < depth)) {
    const next = [];
    frontier.forEach((id) => {
      (adj.get(id) || []).forEach(({to, edgeId}) => {
        edges.add(edgeId);
        if (!nodes.has(to)) {
          nodes.add(to);
          next.push(to);
        }
      });
    });
    frontier = next;
    level += 1;
  }
  return {nodes, edges};
};

/**
 * Highlight the up- and downstream of a selected node/group.
 * @return {{nodeIds:Set, edgeIds:Set, downstreamEdgeIds:Set, upstreamEdgeIds:Set}}
 *   downstream/upstream edge sets drive animation direction; on a cycle an edge
 *   may be in both — downstream wins (caller applies the tie-break, §B2.6).
 */
export const traverseNodes = (nodes, edges, selectedId, {depth = null} = {}) => {
  const empty = {nodeIds: new Set(), edgeIds: new Set(), downstreamEdgeIds: new Set(), upstreamEdgeIds: new Set()};
  if (!selectedId || !nodes.some((n) => n.id === selectedId)) return empty;

  const groupMembers = buildGroupMembers(nodes);
  const seed = expand(selectedId, groupMembers); // group seeds BFS with itself + members (§B2.5)

  const down = bfs(buildAdjacency(edges, groupMembers, false), seed, depth);
  const up = bfs(buildAdjacency(edges, groupMembers, true), seed, depth);

  const nodeIds = new Set([...down.nodes, ...up.nodes]);
  const edgeIds = new Set([...down.edges, ...up.edges]);
  return {nodeIds, edgeIds, downstreamEdgeIds: down.edges, upstreamEdgeIds: up.edges};
};

/**
 * Element-level up/down highlight over the mapping graph (§B2.7). When a hop's
 * parent system edge has no element mapping, traversal continues at NODE
 * granularity through that edge so element lineage never dead-ends at an
 * unmapped hop.
 * @return {{elementKeys:Set, mappingIds:Set, nodeIds:Set}}
 */
export const traverseElements = (nodes, edges, mappings, selectedNodeId, selectedElementId, {depth = null, selectedTableId = null} = {}) => {
  const empty = {elementKeys: new Set(), mappingIds: new Set(), nodeIds: new Set()};
  if (!selectedNodeId || selectedElementId == null) return empty;

  // Element adjacency (both directions), carrying mappingId + the endpoint node.
  const downAdj = new Map();
  const upAdj = new Map();
  const link = (adj, from, entry) => {
    if (!adj.has(from)) adj.set(from, []);
    adj.get(from).push(entry);
  };
  mappings.forEach((m) => {
    const s = {key: elKey(m.sourceNodeId, m.sourceElementId, m.sourceTableId), nodeId: m.sourceNodeId};
    const t = {key: elKey(m.targetNodeId, m.targetElementId, m.targetTableId), nodeId: m.targetNodeId};
    const pairs = m.direction === 'backward' ? [[t, s]] :
      m.direction === 'bidirectional' ? [[s, t], [t, s]] : [[s, t]];
    pairs.forEach(([a, b]) => {
      link(downAdj, a.key, {to: b, mappingId: m.id});
      link(upAdj, b.key, {to: a, mappingId: m.id});
    });
  });

  const elementKeys = new Set();
  const mappingIds = new Set();
  const nodeIds = new Set([selectedNodeId]);
  const walk = (adj) => {
    const startKey = elKey(selectedNodeId, selectedElementId, selectedTableId);
    elementKeys.add(startKey);
    let frontier = [{key: startKey, nodeId: selectedNodeId}];
    let level = 0;
    while (frontier.length && (depth == null || level < depth)) {
      const next = [];
      frontier.forEach(({key}) => {
        (adj.get(key) || []).forEach(({to, mappingId}) => {
          mappingIds.add(mappingId);
          if (!elementKeys.has(to.key)) {
            elementKeys.add(to.key);
            nodeIds.add(to.nodeId);
            next.push(to);
          }
        });
      });
      frontier = next;
      level += 1;
    }
  };
  walk(downAdj);
  walk(upAdj);

  // Node-granularity continuation across mapping-less system edges (§B2.7):
  // seed a node-level traversal from every node that owns a highlighted element.
  const mappedEdgeIds = new Set(mappings.map((m) => m.edgeId));
  const edgesWithoutMappings = edges.filter((e) => !mappedEdgeIds.has(e.id));
  if (edgesWithoutMappings.length) {
    [...nodeIds].forEach((nodeId) => {
      const {nodeIds: reached} = traverseNodes(nodes, edgesWithoutMappings, nodeId, {depth});
      reached.forEach((id) => nodeIds.add(id));
    });
  }
  return {elementKeys, mappingIds, nodeIds};
};
